import { defineStore } from 'pinia'
import api from '../lib/axios'

/**
 * What the signed-in user may reach, for hiding nav items the backend would refuse.
 *
 * This is cosmetic and must stay cosmetic. Hiding a link restricts nothing — the route is
 * still there for a direct URL, a deep link or a fetch — so every endpoint refuses on its
 * own and this only decides what is worth showing. Never the reverse.
 *
 * `enforced` is why a half-filled matrix cannot blank the app: until a module's switch is on
 * its grid describes an intention rather than a rule, so nothing is hidden for it. Anything
 * unknown is shown, for the same reason — a nav that disappears because a request failed is
 * worse than one that offers a link the server then refuses with a clear message.
 */
export const usePermissionsStore = defineStore('permissions', {
    state: () => ({
        grid: {},
        enforced: [],
        companyId: null,
        loaded: false,
    }),

    actions: {
        async load() {
            try {
                const { data } = await api.get('/me/permissions')
                const payload = data.data ?? data
                this.grid = payload.grid ?? {}
                this.enforced = payload.enforced ?? []
                this.companyId = payload.company_id ?? null
            } catch {
                // Show everything rather than hide everything. The endpoints still refuse.
                this.grid = {}
                this.enforced = []
            } finally {
                this.loaded = true
            }
        },

        isEnforced(moduleKey) {
            return this.enforced.includes(moduleKey)
        },

        /**
         * May this person reach it? `true` whenever the answer is not a definite no.
         *
         * @param {string} moduleKey
         * @param {string|null} sub  a sub-module name, or null for the module itself
         * @param {'read'|'write'} level
         */
        can(moduleKey, sub = null, level = 'read') {
            if (!moduleKey || !this.isEnforced(moduleKey)) return true

            const RANK = { none: 0, read: 1, write: 2 }
            const value = this.grid[`${moduleKey}|${sub ?? '*'}`] ?? 'none'

            return (RANK[value] ?? 0) >= (RANK[level] ?? 1)
        },

        /** Is any part of this module reachable? Used to hide a whole module switcher entry. */
        canSeeModule(moduleKey) {
            if (!moduleKey || !this.isEnforced(moduleKey)) return true

            return Object.entries(this.grid).some(
                ([key, value]) => key.startsWith(`${moduleKey}|`) && !key.endsWith('|*') && value !== 'none',
            )
        },
    },
})
